import { AppDataTable, AppSectionTitle } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { useOpportunityStatuses } from '@opportunities/-hooks/useOpportunityStatuses'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'
import { OPPORTUNITY_TYPE_IDS } from 'shared'
import { z } from 'zod'
import { useAuthStore } from '@/stores/auth'
import { CloseReasonDialog } from './-components/CloseReasonDialog'
import { CommentsDialog } from './-components/CommentsDialog'
import { CopyDialog } from './-components/CopyDialog'
import { CustomSearchDialog } from './-components/CustomSearchDialog'
import { GCompanyDialog } from './-components/GCompanyDialog'
import { TableActions } from './-components/TableActions'
import { TabSelector } from './-components/TabSelector'
import { TagsDialog } from './-components/TagsDialog'
import { getDefaultTabKey, opportunityTabs } from './-components/tabs'
import { useLastCheckAt } from './-hooks/useLastCheckAt'
import { useOpportunities } from './-hooks/useOpportunities'
import { useOpportunityColumns } from './-hooks/useOpportunityColumns'
import { useOpportunityDialogs } from './-hooks/useOpportunityDialogs'
import { useOpportunityFilter } from './-hooks/useOpportunityFilter'
import { useOpportunityStatusChange } from './-hooks/useOpportunityStatusChange'
import { useOpportunityTableState } from './-hooks/useOpportunityTableState'
import { useOpportunityUrlSync } from './-hooks/useOpportunityUrlSync'

const opportunitiesSearchSchema = z.object({
  tab: z.string().optional(),
  search: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/opportunities/list/')({
  component: OpportunitiesList,
  validateSearch: opportunitiesSearchSchema,
  staticData: {
    breadcrumbLabel: 'オポチュニティ一覧',
  },
})

function OpportunitiesList() {
  const user = useAuthStore((state) => state.user)
  const searchParams = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const defaultTab = useMemo(() => {
    if (!user) return 'owner'
    return getDefaultTabKey({
      isCaptain: user.isCaptain,
      acl: user.acl,
    })
  }, [user])

  const { selectedTab, searchText, setSearchText, handleTabChange } = useOpportunityUrlSync({
    searchParamsTab: searchParams.tab,
    searchParamsSearch: searchParams.search,
    defaultTab,
    navigate,
    user,
  })

  const { pageSize, sortKey, sortDirection, handleSortChange, handlePageSizeChange } = useOpportunityTableState({
    selectedTab,
  })

  const {
    searchDialogOpen,
    setSearchDialogOpen,
    commentDialogOpen,
    setCommentDialogOpen,
    tagDialogOpen,
    setTagDialogOpen,
    copyDialogOpen,
    setCopyDialogOpen,
    closeReasonDialogOpen,
    setCloseReasonDialogOpen,
    gCompanyDialogOpen,
    setGCompanyDialogOpen,
    selectedOpportunity,
    selectedCopyOpportunity,
    setSelectedCopyOpportunity,
    selectedStatusOpportunity,
    setSelectedStatusOpportunity,
    pendingStatusId,
    setPendingStatusId,
    handleCommentClick,
    handleTagEditClick,
    handleCopyClick,
  } = useOpportunityDialogs()

  const lastCheckAt = useLastCheckAt()

  const { data, isLoading, error } = useOpportunities({ tabKey: selectedTab })

  const { data: statusesData } = useOpportunityStatuses()
  const opportunityStatuses = statusesData?.statuses || []

  const { filteredOpportunities, handleTagClick } = useOpportunityFilter({
    opportunities: data?.opportunities || [],
    searchText,
    setSearchText,
  })

  const { handleStatusChangeClick, handleCloseReasonSubmit, handleGCompanySubmit } = useOpportunityStatusChange({
    opportunityStatuses,
    onOpenCloseReasonDialog: (opportunity, statusId) => {
      setSelectedStatusOpportunity(opportunity)
      setPendingStatusId(statusId)
      setCloseReasonDialogOpen(true)
    },
    onOpenGCompanyDialog: (opportunity, statusId) => {
      setSelectedStatusOpportunity(opportunity)
      setPendingStatusId(statusId)
      setGCompanyDialogOpen(true)
    },
  })

  const { columns, excelColumns } = useOpportunityColumns({
    selectedTab,
    handlers: {
      onCommentClick: handleCommentClick,
      onTagEditClick: handleTagEditClick,
      onCopyClick: handleCopyClick,
      onStatusChangeClick: handleStatusChangeClick,
      onTagClick: handleTagClick,
    },
    opportunityStatuses,
    lastCheckAt,
    currentMemberId: user?.memberId ?? null,
  })

  return (
    <div className='grid h-full grid-rows-[auto_minmax(0,1fr)]'>
      <AppSectionTitle
        collapsible
        content={
          <div className='space-y-4 p-4'>
            {user && (
              <TabSelector
                tabs={opportunityTabs}
                value={selectedTab}
                onChange={handleTabChange}
                user={{
                  isCaptain: user.isCaptain,
                  isServiceManager: user.isServiceManager,
                  isProjectManager: user.isProjectManager,
                  isSalesMember: user.isSalesMember,
                  acl: user.acl,
                }}
              />
            )}
            <TableActions
              searchText={searchText}
              onSearchTextChange={setSearchText}
              opportunities={data?.opportunities || []}
              opportunityStatuses={opportunityStatuses}
              onOpenSearchDialog={() => setSearchDialogOpen(true)}
              canAddOpportunity={!!user?.acl?.opportunity?.create}
              selectedTab={selectedTab}
              onAddOpportunity={() => {
                setSelectedCopyOpportunity(null)
                setCopyDialogOpen(true)
              }}
              filteredOpportunities={filteredOpportunities}
              excelColumns={excelColumns}
              sortKey={sortKey}
              sortDirection={sortDirection}
              sortColumns={columns}
            />
          </div>
        }
      >
        オポチュニティ一覧
      </AppSectionTitle>

      <AppDataTable
        data={filteredOpportunities}
        columns={columns}
        isLoading={isLoading}
        errorMessage={error ? `データの取得に失敗しました: ${error.message}` : undefined}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[100, 200, 300]}
        emptyMessage='オポチュニティがありません'
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        getRowKey={(row) => row.id}
        columnSelector={{ cookieKey: 'header-select-opportunities' }}
      />

      <CustomSearchDialog
        open={searchDialogOpen}
        onOpenChange={setSearchDialogOpen}
        opportunities={data?.opportunities || []}
        opportunityStatuses={opportunityStatuses}
        onSearch={setSearchText}
        currentSearchText={searchText}
      />

      <CommentsDialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen} opportunity={selectedOpportunity} />

      <TagsDialog open={tagDialogOpen} onOpenChange={setTagDialogOpen} opportunity={selectedOpportunity} />

      <CopyDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        opportunity={selectedCopyOpportunity}
        opportunityTypeId={selectedTab === 'captain' ? OPPORTUNITY_TYPE_IDS.CAPTAIN : OPPORTUNITY_TYPE_IDS.SALES}
      />

      <CloseReasonDialog
        open={closeReasonDialogOpen}
        onOpenChange={setCloseReasonDialogOpen}
        opportunity={selectedStatusOpportunity}
        onSubmit={(data) => {
          if (!selectedStatusOpportunity || !pendingStatusId) return
          handleCloseReasonSubmit(selectedStatusOpportunity.id, pendingStatusId, data)
          setSelectedStatusOpportunity(null)
          setPendingStatusId(null)
        }}
      />

      <GCompanyDialog
        open={gCompanyDialogOpen}
        onOpenChange={setGCompanyDialogOpen}
        opportunity={selectedStatusOpportunity}
        onSubmit={(data) => {
          if (!selectedStatusOpportunity || !pendingStatusId) return
          handleGCompanySubmit(selectedStatusOpportunity.id, pendingStatusId, data)
          setSelectedStatusOpportunity(null)
          setPendingStatusId(null)
        }}
      />
    </div>
  )
}
